/**
 * <TierGate> — shared upsell surface for HTTP 402 (tier_required) responses.
 *
 * The backend's `require_tier()` dependency returns a structured detail:
 *   {
 *     code: "tier_required",
 *     required_tier: "pro" | "pro_plus",
 *     current_tier: "free" | "pro",
 *     message: string,
 *     upgrade_url: "/pricing",
 *     feature?: string  // optional context tag (e.g. "csv_import_quota")
 *   }
 *
 * Three rendering variants — pick the one that fits the surface:
 *
 *   1. <TierGate.Card detail bullets? title?> — inline card replacing forbidden
 *      content (e.g. the spending donut for free users).
 *   2. <TierGate.Dialog open onOpenChange detail bullets? title?> — modal
 *      surfaced after a 402 response (e.g. on the import button click).
 *   3. <TierGate.Inline detail /> — compact 1-liner above existing content.
 *
 * Tier styling is derived from required_tier so the gradient matches the
 * pricing page (Steel = slate→ink, Damascus = blue→violet).
 */
import React from 'react';
import { Mail, ArrowRight, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const TIER_LABEL = { pro: 'Steel', pro_plus: 'Damascus' };
const TIER_GRADIENTS = {
  pro:      'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
  pro_plus: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
};

const DEFAULT_BULLETS = {
  pro: [
    'Unlimited accounts & recurring transactions',
    '18-month future balance forecast',
    'CSV / Excel import + auto-reconcile from statements',
    'Email any export, plus the spending donut',
  ],
  pro_plus: [
    'Everything in Steel, plus:',
    '36-month future balance forecast',
    'Early access to new features',
    'Shared accounts + Open Banking auto-sync (planned)',
  ],
};

const _tierKey = (detail) => (detail?.required_tier === 'pro_plus' ? 'pro_plus' : 'pro');
const _tierName = (detail) => TIER_LABEL[_tierKey(detail)] || 'Steel';
const _tierGrad = (detail) => TIER_GRADIENTS[_tierKey(detail)] || TIER_GRADIENTS.pro;
const _upgradeUrl = (detail) => detail?.upgrade_url || '/pricing';

const TierPill = ({ detail, icon: Icon = Lock, label }) => (
  <span
    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider w-fit"
    style={{ backgroundImage: _tierGrad(detail) }}
    data-testid={`tier-gate-pill-${_tierKey(detail)}`}
  >
    <Icon className="w-3 h-3" />
    {label || `${_tierName(detail)} feature`}
  </span>
);

const BulletList = ({ items }) => (
  <ul className="mt-3 space-y-2 text-sm text-[#334155]">
    {items.map((b) => (
      <li key={b} className="flex items-start gap-2">
        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#0052FF] inline-block flex-shrink-0" />
        <span>{b}</span>
      </li>
    ))}
  </ul>
);

const CtaRow = ({ detail, onDismiss, dismissLabel = 'Maybe later', testIdPrefix }) => (
  <div className="flex gap-3 pt-2">
    <Button
      type="button"
      onClick={() => { if (onDismiss) onDismiss(); window.location.href = _upgradeUrl(detail); }}
      className="flex-1 text-white hover:opacity-90 border-0"
      style={{ backgroundImage: _tierGrad(detail), backgroundColor: 'transparent' }}
      data-testid={`${testIdPrefix}-cta`}
    >
      See {_tierName(detail)}{_tierName(detail) === 'Steel' ? ' & Damascus' : ''} plans
      <ArrowRight className="w-4 h-4 ml-1" />
    </Button>
    {onDismiss && (
      <Button
        type="button"
        onClick={onDismiss}
        className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
        data-testid={`${testIdPrefix}-dismiss`}
      >
        {dismissLabel}
      </Button>
    )}
  </div>
);

const Card = ({ detail, title, bullets, testIdPrefix = 'tier-gate' }) => {
  const items = bullets || DEFAULT_BULLETS[_tierKey(detail)] || DEFAULT_BULLETS.pro;
  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-xl p-6"
      data-testid={`${testIdPrefix}-card`}
    >
      <TierPill detail={detail} />
      <h3 className="mt-3 text-xl font-extrabold text-[#020617]" data-testid={`${testIdPrefix}-card-title`}>
        {title || `Unlock with Forge Ledger ${_tierName(detail)}`}
      </h3>
      {detail?.message && (
        <p className="text-sm text-[#64748B] mt-1.5">{detail.message}</p>
      )}
      <BulletList items={items} />
      <div className="mt-5">
        <Button
          type="button"
          onClick={() => { window.location.href = _upgradeUrl(detail); }}
          className="text-white hover:opacity-90 border-0"
          style={{ backgroundImage: _tierGrad(detail), backgroundColor: 'transparent' }}
          data-testid={`${testIdPrefix}-card-cta`}
        >
          See {_tierName(detail)}{_tierName(detail) === 'Steel' ? ' & Damascus' : ''} plans
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

const TierGateDialog = ({ open, onOpenChange, detail, title, bullets, testIdPrefix = 'tier-gate', icon }) => {
  const items = bullets || DEFAULT_BULLETS[_tierKey(detail)] || DEFAULT_BULLETS.pro;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <TierPill detail={detail} icon={icon || Mail} />
          <DialogTitle className="text-2xl font-bold text-[#020617]">
            {title || `Unlock with Forge Ledger ${_tierName(detail)}`}
          </DialogTitle>
          {detail?.message && (
            <DialogDescription className="text-[#64748B] pt-1">{detail.message}</DialogDescription>
          )}
        </DialogHeader>
        <BulletList items={items} />
        <CtaRow
          detail={detail}
          onDismiss={() => onOpenChange && onOpenChange(false)}
          testIdPrefix={`${testIdPrefix}-dialog`}
        />
      </DialogContent>
    </Dialog>
  );
};

const Inline = ({ detail, testIdPrefix = 'tier-gate' }) => (
  <div
    className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3 flex items-center gap-3"
    data-testid={`${testIdPrefix}-inline`}
  >
    <TierPill detail={detail} />
    <p className="text-sm text-[#334155] flex-1">
      {detail?.message || `This feature is part of Forge Ledger ${_tierName(detail)}.`}
    </p>
    <Button
      type="button"
      onClick={() => { window.location.href = _upgradeUrl(detail); }}
      className="text-white hover:opacity-90 border-0"
      style={{ backgroundImage: _tierGrad(detail), backgroundColor: 'transparent' }}
      data-testid={`${testIdPrefix}-inline-cta`}
    >
      Upgrade
    </Button>
  </div>
);

// Convenience helper — read a tier_required detail off an axios error response.
export const tierRequiredDetail = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object' && d.code === 'tier_required') return d;
  return null;
};

const TierGate = { Card, Dialog: TierGateDialog, Inline };
export default TierGate;
